import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Share2, Check, ChevronDown } from 'lucide-react';

interface LeagueHeaderProps {
  showBackButton?: boolean;
  leagueName?: string;
  espnLeagueId?: string;
  season?: number;
  /**
   * Seasons available for this league. When multi-season switching is not wired
   * end-to-end (backend returns latest season only), pass a single season and the
   * switcher renders disabled with an explanatory tooltip.
   */
  availableSeasons?: number[];
  seasonSwitchEnabled?: boolean;
  onSeasonChange?: (season: number) => void;
}

const LeagueHeader: React.FC<LeagueHeaderProps> = ({
  leagueName,
  espnLeagueId,
  season,
  availableSeasons,
  seasonSwitchEnabled = false,
  onSeasonChange,
}) => {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const url = espnLeagueId
      ? `${window.location.origin}/leagues/espn/${espnLeagueId}`
      : window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers/contexts without clipboard API
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
      } catch {
        /* noop */
      }
      document.body.removeChild(el);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }, [espnLeagueId]);

  const seasonDisabled = !seasonSwitchEnabled;

  return (
    <header className="sticky top-0 z-40 bg-background-primary/95 backdrop-blur-sm border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          {/* Left: logo (home) + breadcrumb */}
          <div className="flex items-center min-w-0 gap-3">
            <Link to="/" className="flex items-center flex-shrink-0" aria-label="DraftEdge home">
              <img
                src="/draftedge_logo.png"
                alt="DraftEdge"
                className="h-6 w-auto object-contain sm:h-7"
              />
            </Link>
            {leagueName && (
              <nav className="flex items-center min-w-0 text-sm" aria-label="Breadcrumb">
                <ChevronRight className="h-4 w-4 text-text-tertiary flex-shrink-0" />
                <span className="hidden sm:inline text-text-tertiary flex-shrink-0">DraftEdge</span>
                <ChevronRight className="hidden sm:inline h-4 w-4 text-text-tertiary flex-shrink-0" />
                <Link
                  to={espnLeagueId ? `/leagues/espn/${espnLeagueId}/overview` : '/'}
                  className="truncate font-medium text-text-primary hover:text-accent-primary transition-colors"
                  title={leagueName}
                >
                  {leagueName}
                </Link>
              </nav>
            )}
          </div>

          {/* Right: season switcher + share */}
          {leagueName && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Season switcher — disabled-with-tooltip when multi-season is not wired */}
              {season && (
                <div className="relative group">
                  <button
                    type="button"
                    disabled={seasonDisabled}
                    onClick={
                      seasonDisabled || !onSeasonChange
                        ? undefined
                        : () => {
                            const seasons = availableSeasons || [season];
                            const idx = seasons.indexOf(season);
                            const next = seasons[(idx + 1) % seasons.length];
                            onSeasonChange(next);
                          }
                    }
                    className={`inline-flex items-center gap-1 rounded-lg border border-fieldline px-2.5 py-1.5 font-mono text-xs transition-colors ${
                      seasonDisabled
                        ? 'text-text-tertiary cursor-not-allowed'
                        : 'text-text-primary hover:border-accent-primary hover:text-accent-primary'
                    }`}
                    aria-label="Season"
                  >
                    {season}
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  {seasonDisabled && (
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute right-0 top-full mt-1.5 whitespace-nowrap rounded-md border border-fieldline bg-background-tertiary px-2 py-1 text-[11px] text-text-secondary opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
                    >
                      Multi-season coming soon
                    </span>
                  )}
                </div>
              )}

              {/* Share button */}
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 rounded-lg border border-fieldline px-2.5 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:border-accent-primary hover:text-accent-primary active:translate-y-px"
                aria-label="Copy league link"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" style={{ color: '#2FBF71' }} />
                    <span style={{ color: '#2FBF71' }}>Copied</span>
                  </>
                ) : (
                  <>
                    <Share2 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Share</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default LeagueHeader;
